import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Collections from "@/pages/Collections";
import Documents from "@/pages/Documents";
import Invoices from "@/pages/Invoices";
import Requests from "@/pages/Requests";
import ChangePassword from "@/pages/ChangePassword";
import { Loader2 } from "lucide-react";

const FullPageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-[var(--hh-band)]">
    <Loader2 className="h-7 w-7 animate-spin text-[color:var(--hh-amber-600)]" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, initializing } = useAuth();
  if (initializing) return <FullPageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
};

const UnauthorizedRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => navigate("/login", { replace: true });
    window.addEventListener("hh-unauthorized", handler);
    return () => window.removeEventListener("hh-unauthorized", handler);
  }, [navigate]);
  return null;
};

function AppRoutes() {
  const { isAuthenticated, initializing } = useAuth();
  return (
    <>
      <UnauthorizedRedirect />
      <Routes>
        <Route
          path="/login"
          element={initializing ? <FullPageLoader /> : isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/collections" element={<ProtectedRoute><Collections /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/requests" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
